import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  BadgeCheck,
  Info,
  ImagePlus,
  Loader2,
  X,
} from "lucide-react";

import { cn, getFileUrl } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useSupportForm } from "../hooks/use-support-form";
import { useSupportStore } from "../store/support-store";
import { SupportSuccessCard } from "../components/SupportSuccessCard";
import { ModelPickerField } from "../components/ModelPickerField";
import { categoryColorClasses } from "../config/category-colors";

const FieldError: React.FC<{ message?: string }> = ({ message }) => {
  if (!message) return null;
  return <p className="text-sm text-destructive">{message}</p>;
};

const getInitials = (name?: string): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const initials = parts.slice(0, 2).map((part) => part[0]?.toUpperCase());
  return initials.join("") || "?";
};

export const ContactFormSection: React.FC = () => {
  const { t } = useTranslation();
  const { lastSubmission } = useSupportStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    form,
    onSubmit,
    isSubmitting,
    categories,
    selectedCategory,
    selectedIssue,
    showModelField,
    attachmentsAllowed,
    messageRequired,
    handleCategoryChange,
    handleIssueChange,
    attachments,
    addAttachments,
    removeAttachment,
    maxAttachments,
    isLoggedIn,
    user,
  } = useSupportForm();

  // ✅ Submit sonrası success ekranı göster
  if (lastSubmission) {
    return <SupportSuccessCard />;
  }

  const { register, watch, formState } = form;
  const { errors } = formState;

  // 1️⃣ Adım: Kategori seçimi — ikonlu kartlar
  if (!selectedCategory) {
    return (
      <div className="flex flex-col gap-4">
        <h2 className="text-base font-semibold text-foreground">
          {t("support.form.selectCategoryTitle")}
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {categories.map((category) => {
            const Icon = category.icon;
            const colors = categoryColorClasses[category.color];
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => handleCategoryChange(category.id)}
                className={cn(
                  "group flex items-start gap-3.5 rounded-2xl border border-border bg-card p-4 text-left shadow-sm transition-all hover:shadow-md",
                  colors.hoverBorder,
                )}
              >
                <div
                  className={cn(
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors",
                    colors.iconWrap,
                  )}
                >
                  <Icon className={cn("h-5 w-5", colors.icon)} />
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold text-foreground">
                    {t(category.labelKey)}
                  </span>
                  <span className="line-clamp-2 min-h-[32px] text-xs text-muted-foreground">
                    {t(category.descriptionKey)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // 2️⃣ Adım: Seçilen kategori için form
  const CategoryIcon = selectedCategory.icon;
  const selectedColors = categoryColorClasses[selectedCategory.color];
  const userImageUrl = user?.image ? getFileUrl(user.image) : null;

  return (
    <div className="flex flex-col gap-4">
      {/* Sol/sağ kolonlardaki başlıklarla aynı yükseklikte dış başlık — kart üst kenarları hizalanır */}
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-foreground">
          {t(selectedCategory.labelKey)}
        </h2>
        <button
          type="button"
          onClick={() => handleCategoryChange("")}
          className="flex shrink-0 items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t("support.form.changeCategory")}
        </button>
      </div>

      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-6 rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8"
        noValidate
      >
        {/* Kategori ikonu + açıklaması */}
        <div className="flex items-center gap-3 border-b border-border pb-4">
          <div
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
              selectedColors.iconWrap,
            )}
          >
            <CategoryIcon className={cn("h-5 w-5", selectedColors.icon)} />
          </div>
          <p className="text-sm text-muted-foreground">
            {t(selectedCategory.descriptionKey)}
          </p>
        </div>

        {/* Sorun Maddesi */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-foreground">
            {t("support.form.issue")}
            <span className="text-destructive"> *</span>
          </label>
          <Select value={watch("issueId")} onValueChange={handleIssueChange}>
            <SelectTrigger>
              <SelectValue placeholder={t("support.form.issuePlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {selectedCategory.issues.map((issue) => (
                <SelectItem key={issue.id} value={issue.id}>
                  {t(issue.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError message={errors.issueId?.message} />
        </div>

        {/* Bilgilendirme banner'ı (madde bazlı) */}
        {selectedIssue?.infoBannerKey && (
          <div className="flex items-start gap-3 rounded-xl bg-indigo-500/10 p-4 text-sm text-foreground dark:bg-indigo-500/15">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600 dark:text-indigo-400" />
            <p>{t(selectedIssue.infoBannerKey)}</p>
          </div>
        )}

        {/* Ad Soyad + Email — login kullanıcıda kompakt kimlik şeridi, misafirde düzenlenebilir alanlar */}
        {isLoggedIn && user ? (
          <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3">
            <Avatar className="h-9 w-9">
              <AvatarImage src={userImageUrl ?? undefined} alt={user.name} />
              <AvatarFallback className="text-xs font-semibold text-muted-foreground">
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col overflow-hidden">
              <span className="truncate text-sm font-medium text-foreground">
                {user.name}
              </span>
              <span className="truncate text-xs text-muted-foreground">
                {user.email}
              </span>
            </div>
            <span className="ml-auto flex shrink-0 items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <BadgeCheck className="h-3.5 w-3.5 text-emerald-500" />
              {t("support.form.sendingAs")}
            </span>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <label
                htmlFor="support-fullName"
                className="text-sm font-medium text-foreground"
              >
                {t("support.form.fullName")}
                <span className="text-destructive"> *</span>
              </label>
              <Input
                id="support-fullName"
                {...register("fullName")}
                placeholder={t("support.form.fullNamePlaceholder")}
                aria-invalid={!!errors.fullName}
              />
              <FieldError message={errors.fullName?.message} />
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="support-email"
                className="text-sm font-medium text-foreground"
              >
                {t("support.form.email")}
                <span className="text-destructive"> *</span>
              </label>
              <Input
                id="support-email"
                type="email"
                {...register("email")}
                placeholder={t("support.form.emailPlaceholder")}
                aria-invalid={!!errors.email}
              />
              <FieldError message={errors.email?.message} />
            </div>
          </div>
        )}

        {/* Model seçimi (koşullu) — chat'teki aramalı picker ile aynı deneyim, tüm modeller listelenir */}
        {showModelField && (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-foreground">
              {t("support.form.model")}
              {selectedIssue?.model === "required" && (
                <span className="text-destructive"> *</span>
              )}
            </label>
            <ModelPickerField
              value={watch("modelName")}
              onChange={(modelName) =>
                form.setValue("modelName", modelName, { shouldValidate: true })
              }
              placeholder={t("support.form.modelPlaceholder")}
              hasError={!!errors.modelName}
            />
            <FieldError message={errors.modelName?.message} />
          </div>
        )}

        {/* Mesaj */}
        <div className="flex flex-col gap-2">
          <label
            htmlFor="support-message"
            className="text-sm font-medium text-foreground"
          >
            {t("support.form.message")}
            {messageRequired && <span className="text-destructive"> *</span>}
          </label>
          <Textarea
            id="support-message"
            rows={5}
            {...register("message")}
            placeholder={t("support.form.messagePlaceholder")}
            aria-invalid={!!errors.message}
          />
          <FieldError message={errors.message?.message} />
        </div>

        {/* Görsel ekleri (koşullu) */}
        {attachmentsAllowed && (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-foreground">
              {t("support.form.attachments")}
              <span className="ml-1 text-xs font-normal text-muted-foreground">
                {t("support.attachments.hint", { count: maxAttachments })}
              </span>
            </label>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(event) => {
                if (event.target.files?.length) {
                  addAttachments(event.target.files);
                }
                event.target.value = "";
              }}
            />

            <div className="flex flex-wrap items-center gap-3">
              {attachments.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="group relative h-20 w-20 overflow-hidden rounded-xl border border-border"
                >
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeAttachment(index)}
                    className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100"
                    aria-label={t("common.delete")}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}

              {attachments.length < maxAttachments && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border text-muted-foreground transition-colors hover:border-indigo-500 hover:text-indigo-600 dark:hover:border-indigo-400 dark:hover:text-indigo-400"
                >
                  <ImagePlus className="h-5 w-5" />
                  <span className="text-[11px]">
                    {t("support.attachments.add")}
                  </span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Submit */}
        <Button type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {isSubmitting
            ? t("support.form.submitting")
            : t("support.form.submit")}
        </Button>
      </form>
    </div>
  );
};
